'use client';

import { useFormatter, useTranslations } from 'next-intl';
import type { PlayerLook } from '@/lib/api/rewards-hub/models';
import { GlowName } from '@/lib/components/RewardsHub/GlowName';
import { useScaleLookup } from '@/lib/components/RewardsHub/Leaderboard/useScaleLookup';
import { UserAvatar } from '@/lib/ui-kit-v2/user-avatar';
import { EMPTY_VALUE, WHOLE_USD_FORMAT } from '../core/constants';
import { useSearch } from '../core/SearchContext';
import { Highlight } from '../shared/Highlight';
import { MetaDot } from '../shared/MetaDot';
import { RowFigures, RowText, SearchRow } from '../shared/SearchRow';

const AVATAR_SIZE = 24;

export interface TraderRowData {
  userId: string;
  username: string | null;
  look: PlayerLook | null;
  /** Place on the board; `null` when the source has none (the nickname search). */
  position: number | null;
  rankIndex: number | null;
  tasks: number | null;
  volume: number | null;
  /** The line under the volume — "12,201 trades", or what the source can say instead. */
  activity: string | null;
}

interface RankLineProps {
  rankIndex: number;
  tasks: number | null;
}

/** The rank's picture and name, then the tasks done — once the scale has named the rank. */
function RankLine({ rankIndex, tasks }: RankLineProps) {
  const t = useTranslations('search');
  const { rankTitle, rankImage } = useScaleLookup();
  const title = rankTitle(rankIndex);
  if (!title) return null;
  return (
    <>
      <img src={rankImage(rankIndex)} alt="" className="size-3.5 shrink-0 object-contain" />
      <span className="truncate">{title}</span>
      {tasks != null ? (
        <>
          <MetaDot />
          <span className="shrink-0">{t('tasks', { count: tasks })}</span>
        </>
      ) : null}
    </>
  );
}

interface TraderRowProps {
  trader: TraderRowData;
}

/** A trader: place, portrait, nickname, rank and tasks, then their volume. */
export function TraderRow({ trader }: TraderRowProps) {
  const t = useTranslations('search');
  const format = useFormatter();
  const { select } = useSearch();
  const name = trader.username ?? t('noNickname');
  const href = `/stats/profile/${trader.userId}`;

  const handleSelect = () =>
    select({ id: `traders:${trader.userId}`, kind: 'traders', label: name, href });

  const title = (
    <GlowName glow={trader.look?.glow ?? null}>
      <Highlight text={name} />
    </GlowName>
  );
  const rank =
    trader.rankIndex == null ? undefined : (
      <RankLine rankIndex={trader.rankIndex} tasks={trader.tasks} />
    );

  return (
    <SearchRow href={href} onSelect={handleSelect}>
      {trader.position == null ? null : (
        <span className="w-2.5 shrink-0 text-[12px] font-medium leading-4 text-v2-text-primary tabular-nums">
          {format.number(trader.position)}
        </span>
      )}
      <UserAvatar userId={trader.userId} name={name} look={trader.look} size={AVATAR_SIZE} />
      <RowText title={title} meta={rank} />
      <RowFigures
        value={trader.volume == null ? EMPTY_VALUE : format.number(trader.volume, WHOLE_USD_FORMAT)}
        detail={trader.activity}
      />
    </SearchRow>
  );
}
